'use client';

import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import { createLibraryCollections, type LibraryCollections } from '@/lib/library/collections';

interface LibraryContextType {
  collections: LibraryCollections | null;
  isAuthenticated: boolean;
  userId: string | undefined;
}

const LibraryContext = createContext<LibraryContextType | null>(null);

interface LibraryProviderProps {
  userId: string | undefined;
  isAuthenticated: boolean;
  children: React.ReactNode;
}

export function LibraryProvider({
  userId,
  isAuthenticated,
  children,
}: LibraryProviderProps) {
  const queryClient = useQueryClient();

  const collections = useMemo(() => {
    if (isAuthenticated && userId) {
      return createLibraryCollections(userId, queryClient);
    }
    return null;
  }, [isAuthenticated, userId, queryClient]);

  return (
    <LibraryContext.Provider value={{ collections, isAuthenticated, userId }}>
      {children}
    </LibraryContext.Provider>
  );
}

export function useLibraryContext(): LibraryContextType {
  const context = useContext(LibraryContext);
  if (!context) {
    throw new Error('useLibraryContext must be used within a LibraryProvider');
  }
  return context;
}

export function useLibraryCollections(): LibraryCollections | null {
  return useLibraryContext().collections;
}

export function useLibraryAuth(): boolean {
  return useLibraryContext().isAuthenticated;
}
